import type { AdminFlowToolMenuProps, AdminFlowToolMenuPosition } from "../../models/admin-flow-whiteboard.models";
import type { AdminFlowTool } from "../../models/admin-flows.models";
import { ADMIN_FLOW_TOOL_GROUPS } from "../../utils/admin-flow-whiteboard.constants";
import { getAdminFlowToolGroup } from "../../utils/admin-flow-whiteboard.utils";
import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

// Offer the caller's permitted tools beside the trigger, clear of the scrolling editor panels.
// Hover is a shortcut; click, touch and keyboard focus offer the same selection path.
const AdminFlowToolMenu = ({
  tools,
  value,
  label,
  description,
  allowProcessing = true,
  testId,
  onSelect,
}: AdminFlowToolMenuProps) => {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout>>();
  const focusMenu = useRef(false);
  const [position, setPosition] = useState<AdminFlowToolMenuPosition>();
  const [group, setGroup] = useState("Business");
  const menuId = useId();
  const permittedTools = tools.filter((tool) => tool.executionPolicy !== "native_email_action");
  const groupTools = permittedTools.filter((tool) => getAdminFlowToolGroup(tool) === group);
  const subcategories = Array.from(new Set(groupTools.map((tool) => tool.subcategory || "Tools")));

  const keepOpen = () => clearTimeout(closeTimer.current);
  const closeSoon = () => {
    keepOpen();
    closeTimer.current = setTimeout(() => setPosition(undefined), 180);
  };
  const open = (focus = false) => {
    keepOpen();
    focusMenu.current = focus;
    const rect = triggerRef.current?.getBoundingClientRect();

    if (!rect) return;

    const width = Math.min(520, window.innerWidth - 16);
    setPosition({
      left: Math.max(8, Math.min(rect.right, window.innerWidth - width - 8)),
      top: Math.max(8, Math.min(rect.top, window.innerHeight - 368)),
      width,
    });
  };
  const select = (tool: AdminFlowTool | undefined) => {
    onSelect(tool);
    setPosition(undefined);
    triggerRef.current?.focus();
  };

  useEffect(() => () => clearTimeout(closeTimer.current), []);

  useLayoutEffect(() => {
    if (position && focusMenu.current) {
      focusMenu.current = false;
      menuRef.current?.querySelector<HTMLButtonElement>("button")?.focus();
    }
  }, [position]);

  useEffect(() => {
    if (!position) return;

    const closeOutside = (event: PointerEvent) => {
      const target = event.target as Node;

      if (!triggerRef.current?.contains(target) && !menuRef.current?.contains(target)) setPosition(undefined);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;

      event.preventDefault();
      setPosition(undefined);
      triggerRef.current?.focus();
    };
    const closeOnScroll = (event: Event) => {
      if (!menuRef.current?.contains(event.target as Node)) setPosition(undefined);
    };
    const close = () => setPosition(undefined);
    document.addEventListener("pointerdown", closeOutside);
    document.addEventListener("keydown", closeOnEscape);
    document.addEventListener("scroll", closeOnScroll, true);
    window.addEventListener("resize", close);

    return () => {
      document.removeEventListener("pointerdown", closeOutside);
      document.removeEventListener("keydown", closeOnEscape);
      document.removeEventListener("scroll", closeOnScroll, true);
      window.removeEventListener("resize", close);
    };
  }, [position]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        data-test={testId}
        aria-label={description ? `${label} ${description}` : label}
        aria-expanded={Boolean(position)}
        aria-controls={position ? menuId : undefined}
        aria-haspopup="dialog"
        onMouseEnter={() => open()}
        onMouseLeave={closeSoon}
        onFocus={keepOpen}
        onBlur={(event) => {
          if (!menuRef.current?.contains(event.relatedTarget as Node)) closeSoon();
        }}
        onClick={(event) => open(event.detail === 0)}
        onKeyDown={(event) => {
          if (event.key === "ArrowDown" || event.key === "ArrowRight") {
            event.preventDefault();
            open(true);
          }
        }}
        className="w-full rounded border border-gray-300 bg-white p-2 text-left text-xs hover:border-green focus-visible:outline focus-visible:outline-2 focus-visible:outline-green dark:border-gray-600 dark:bg-gray-800"
      >
        <span className="flex items-center justify-between gap-2">
          <span className="truncate font-semibold">{label}</span>
          <span aria-hidden="true">▸</span>
        </span>
        {description && (
          <span className="mt-1 block text-[11px] leading-4 text-gray-500 dark:text-gray-400">{description}</span>
        )}
      </button>
      {position &&
        createPortal(
          <div
            id={menuId}
            ref={menuRef}
            role="dialog"
            aria-label="Select AI tool"
            data-test={`${testId}-menu`}
            onMouseEnter={keepOpen}
            onMouseLeave={closeSoon}
            onFocusCapture={keepOpen}
            onBlurCapture={(event) => {
              if (
                !event.currentTarget.contains(event.relatedTarget as Node) &&
                event.relatedTarget !== triggerRef.current
              )
                closeSoon();
            }}
            style={position}
            className="fixed z-50 flex max-h-[min(360px,calc(100vh-16px))] rounded border border-gray-200 bg-white p-2 text-xs shadow-xl dark:border-gray-600 dark:bg-gray-800"
          >
            <div
              className="w-40 shrink-0 space-y-1 border-r border-gray-200 pr-2 dark:border-gray-600"
              aria-label="Tool groups"
            >
              {ADMIN_FLOW_TOOL_GROUPS.map((entry) => (
                <button
                  key={entry}
                  type="button"
                  data-test={`${testId}-group-${entry}`}
                  aria-pressed={group === entry}
                  onMouseEnter={() => setGroup(entry)}
                  onFocus={() => setGroup(entry)}
                  onClick={() => setGroup(entry)}
                  className={`flex w-full items-center justify-between gap-1 rounded px-2 py-2 text-left ${group === entry ? "bg-[#E9F4F3] text-green dark:bg-green/20" : "hover:bg-gray-100 dark:hover:bg-gray-700"}`}
                >
                  <span>{entry}</span>
                  <span aria-hidden="true">▸</span>
                </button>
              ))}
            </div>
            <div className="min-w-0 flex-1 overflow-y-auto overscroll-contain pl-2" aria-label={`${group} tools`}>
              {allowProcessing && (
                <button
                  type="button"
                  data-test={`${testId}-analyze`}
                  onClick={() => select(undefined)}
                  className="w-full rounded p-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  Analyze connected inputs only
                </button>
              )}
              {subcategories.map((subcategory) => (
                <div key={subcategory}>
                  <p className="px-2 pb-1 pt-3 font-semibold text-gray-500">{subcategory}</p>
                  {groupTools
                    .filter((tool) => (tool.subcategory || "Tools") === subcategory)
                    .map((tool) => (
                      <button
                        key={tool.name}
                        type="button"
                        data-test={`${testId}-tool-${tool.name}`}
                        aria-pressed={value === tool.name}
                        title={tool.description}
                        onClick={() => select(tool)}
                        className="block w-full break-words rounded p-2 text-left hover:bg-[#E9F4F3] focus-visible:bg-[#E9F4F3] dark:hover:bg-gray-700 dark:focus-visible:bg-gray-700"
                      >
                        {tool.name}
                      </button>
                    ))}
                </div>
              ))}
              {!groupTools.length && <p className="p-2 text-gray-500">No permitted tools in this group.</p>}
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};

export default AdminFlowToolMenu;
